import types from '../mutation-types';
import KanbanAPI from '../../api/kanban';
import { throwErrorMessage } from '../utils/api';

export const state = {
  records: [],
  activeBoard: null,
  uiFlags: {
    isFetching: false,
    isFetchingItem: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
  },
};

export const getters = {
  getBoards($state) {
    return $state.records;
  },
  getActiveBoard($state) {
    return $state.activeBoard;
  },
  getUIFlags($state) {
    return $state.uiFlags;
  },
};

export const actions = {
  get: async ({ commit }) => {
    commit(types.SET_KANBAN_UI_FLAG, { isFetching: true });
    try {
      const response = await KanbanAPI.get();
      commit(types.SET_KANBAN_BOARDS, response.data);
    } catch (error) {
      // Ignore error
    } finally {
      commit(types.SET_KANBAN_UI_FLAG, { isFetching: false });
    }
  },
  getBoard: async ({ commit }, boardId) => {
    commit(types.SET_KANBAN_UI_FLAG, { isFetchingItem: true });
    try {
      const response = await KanbanAPI.show(boardId);
      commit(types.SET_ACTIVE_KANBAN_BOARD, response.data);
    } catch (error) {
      // Ignore error
    } finally {
      commit(types.SET_KANBAN_UI_FLAG, { isFetchingItem: false });
    }
  },
  createBoard: async ({ commit }, board) => {
    commit(types.SET_KANBAN_UI_FLAG, { isCreating: true });
    try {
      const response = await KanbanAPI.create(board);
      commit(types.ADD_KANBAN_BOARD, response.data);
      return response.data;
    } catch (error) {
      return throwErrorMessage(error);
    } finally {
      commit(types.SET_KANBAN_UI_FLAG, { isCreating: false });
    }
  },
  updateBoard: async ({ commit }, { id, ...board }) => {
    commit(types.SET_KANBAN_UI_FLAG, { isUpdating: true });
    try {
      const response = await KanbanAPI.update(id, board);
      commit(types.EDIT_KANBAN_BOARD, response.data);
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_KANBAN_UI_FLAG, { isUpdating: false });
    }
  },
  deleteBoard: async ({ commit }, id) => {
    commit(types.SET_KANBAN_UI_FLAG, { isDeleting: true });
    try {
      await KanbanAPI.delete(id);
      commit(types.DELETE_KANBAN_BOARD, id);
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_KANBAN_UI_FLAG, { isDeleting: false });
    }
  },
  createColumn: async ({ dispatch }, { boardId, column }) => {
    try {
      await KanbanAPI.createColumn(boardId, column);
      await dispatch('getBoard', boardId);
    } catch (error) {
      throwErrorMessage(error);
    }
  },
  updateColumn: async ({ dispatch }, { boardId, columnId, column }) => {
    try {
      await KanbanAPI.updateColumn(boardId, columnId, column);
      await dispatch('getBoard', boardId);
    } catch (error) {
      throwErrorMessage(error);
    }
  },
  deleteColumn: async ({ dispatch }, { boardId, columnId }) => {
    try {
      await KanbanAPI.deleteColumn(boardId, columnId);
      await dispatch('getBoard', boardId);
    } catch (error) {
      throwErrorMessage(error);
    }
  },
  reorderColumns: async ({ commit, dispatch }, { boardId, columns }) => {
    const positionsHash = columns.reduce(
      (hash, column, index) => ({ ...hash, [column.id]: index }),
      {}
    );
    commit(types.SET_KANBAN_COLUMNS, columns);
    try {
      await KanbanAPI.reorderColumns(boardId, positionsHash);
    } catch (error) {
      await dispatch('getBoard', boardId);
      throwErrorMessage(error);
    }
  },
  createTask: async ({ dispatch }, { boardId, task }) => {
    try {
      await KanbanAPI.createTask(boardId, task);
      await dispatch('getBoard', boardId);
    } catch (error) {
      throwErrorMessage(error);
    }
  },
  updateTask: async ({ dispatch }, { boardId, taskId, task }) => {
    try {
      await KanbanAPI.updateTask(boardId, taskId, task);
      await dispatch('getBoard', boardId);
    } catch (error) {
      throwErrorMessage(error);
    }
  },
  deleteTask: async ({ dispatch }, { boardId, taskId }) => {
    try {
      await KanbanAPI.deleteTask(boardId, taskId);
      await dispatch('getBoard', boardId);
    } catch (error) {
      throwErrorMessage(error);
    }
  },
  moveTask: async (
    { commit, dispatch },
    { boardId, taskId, columnId, position, columns }
  ) => {
    // columns carry the optimistic state computed by the drag interaction
    commit(types.SET_KANBAN_COLUMNS, columns);
    try {
      await KanbanAPI.moveTask(boardId, taskId, { columnId, position });
    } catch (error) {
      await dispatch('getBoard', boardId);
      throwErrorMessage(error);
    }
  },
};

export const mutations = {
  [types.SET_KANBAN_UI_FLAG]($state, data) {
    $state.uiFlags = {
      ...$state.uiFlags,
      ...data,
    };
  },
  [types.SET_KANBAN_BOARDS]($state, records) {
    $state.records = records;
  },
  [types.ADD_KANBAN_BOARD]($state, record) {
    $state.records.push(record);
  },
  [types.EDIT_KANBAN_BOARD]($state, record) {
    $state.records = $state.records.map(item =>
      item.id === record.id ? record : item
    );
    if ($state.activeBoard?.id === record.id) {
      $state.activeBoard = { ...$state.activeBoard, ...record };
    }
  },
  [types.DELETE_KANBAN_BOARD]($state, id) {
    $state.records = $state.records.filter(item => item.id !== id);
  },
  [types.SET_ACTIVE_KANBAN_BOARD]($state, board) {
    $state.activeBoard = board;
  },
  [types.SET_KANBAN_COLUMNS]($state, columns) {
    if (!$state.activeBoard) return;
    $state.activeBoard = { ...$state.activeBoard, columns };
  },
};

export default {
  namespaced: true,
  actions,
  state,
  getters,
  mutations,
};
